import React, { useEffect, useState } from "react"
import { Dialog, DialogContent, Typography, Box, Grid, IconButton, useTheme } from "@mui/material"
import { styled } from "@mui/system"
import AppButton from "components/StyledComponents/AppButton"
import FieldsStepper from "../FieldsStepper"
import { ArrowBack, ArrowForward, Close } from "@mui/icons-material"
import AddNewRestaurant from "../FieldSteps/AddNewRestaurant"
import { Formik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import { createCategoryItemsAction, editCategoryItemsAction } from "redux/hotel/action"
import { hotelAction } from "redux/hotel/slice/user"
import { days } from "assets/json/DayNames"
import * as Yup from "yup"
import {
  excludeSections,
  filterSections,
  filterTypeSections,
  getFieldsName,
  stepKeyMap
} from "../restaurantFormUtils"
import { getDidYouKnowPayload, getHistoryPayload } from "helpers/functions"
import { did_you_know_initial_values, history_initial_values } from "helpers/constant"
import { marketingActions } from "redux/marketing/slice/user"

const buttonText = {
  CANCEL: "Cancel",
  SAVE: "Save",
  UPDATE: "Update"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.white.main,
    borderRadius: "8px"
  }
}))

const FieldsModal = ({ open, isEdit, onClose = () => {}, dynamicFieldsGroup, title }) => {
  const stepTitles = [
    isEdit ? "Edit restaurant info" : "Add new restaurant info",
    isEdit ? "Edit hours of operations " : "Add hours of operations ",
    isEdit ? "Edit signature dishes" : "Add signature dishes",
    isEdit ? "Edit chef information" : "Add chef information",
    isEdit ? "Edit new Guest room info" : "Add new Guest room info"
  ]
  const theme = useTheme()
  const dispatch = useDispatch()
  const {
    linkNestedCategory,
    hotelService,
    nestedSubcategoryData,
    categoryItemDetails,
    categoryImageId,
    imageIds,
    signatureDishImages
  } = useSelector((store) => store?.hotel)
  const { didYouKnowImage, soundbites } = useSelector((store) => store?.marketing ?? {})
  const [activeStep, setActiveStep] = useState(0)

  const style = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "15px",
      right: "15px",
      backgroundColor: theme.palette.background.main
    }
  }

  const getStepData = (index) => {
    if (!Array.isArray(dynamicFieldsGroup)) return []

    const matchesKeyword = (key = "", keyword = "") =>
      key.toLowerCase().includes(keyword.toLowerCase())

    switch (index) {
      case 0:
        return dynamicFieldsGroup?.filter(
          (section) =>
            !matchesKeyword(section.key, "signature_dish") &&
            !matchesKeyword(section.type, "chef") &&
            !matchesKeyword(section.key, "chef") &&
            !matchesKeyword(section.type, "daily_hours")
        )

      case 1:
        return dynamicFieldsGroup?.filter((section) => matchesKeyword(section.type, "daily_hours"))

      case 2:
        return dynamicFieldsGroup?.filter((section) =>
          matchesKeyword(section.key, "signature_dish")
        )

      case 3:
        return dynamicFieldsGroup?.filter(
          (section) => matchesKeyword(section.key, "chef") || matchesKeyword(section.type, "chef")
        )

      default:
        return []
    }
  }

  const handleClose = () => {
    onClose()
    setActiveStep(0)
  }

  const handleSubmitForm = (values) => {
    const { fields = {}, signature_dishes = [], chef = [], daily_hours = [] } = values

    const dynamic_fields = []

    dynamicFieldsGroup?.forEach((section) => {
      const { key, type, label, show_listing = false } = section

      if (key === "common") return

      if (type === "week_time_range") {
        const timings = (fields?.[key] || []).map((item) => ({
          ...item,
          opening_time: item.opening_time || "12:00",
          closing_time: item.closing_time || "12:00"
        }))

        dynamic_fields.push({
          key,
          type,
          label,
          value: timings,
          show_listing
        })
      } else if (type === "daily_hours") {
        dynamic_fields.push({
          key,
          type,
          label,
          value: daily_hours || [],
          show_listing
        })
      } else if (key === "signature_dishes") {
        const sectionFields = section?.fields || []
        const enrichedDishes = (signature_dishes || [])?.map((dish, index) => {
          const updatedDish = { ...dish }
          sectionFields.forEach((field) => {
            if (field.type === "image") {
              updatedDish[field.key] = signatureDishImages?.[index]?.[field.key]
                ? signatureDishImages?.[index]?.[field.key]
                : [dish[field.key]?.id] || ""
            }
          })
          return updatedDish
        })

        dynamic_fields.push({
          key,
          type,
          label,
          value: enrichedDishes || [],
          show_listing
        })
      } else if (key?.toLowerCase().includes("chef")) {
        const sectionFields = section?.fields?.map((field) => {
          const fieldType = field?.type?.toLowerCase()
          const formValue = chef?.[section.key]?.[field.key]
          const imageIdValue = imageIds?.[key]?.[field.key]
          let value = ""

          const useImageIds = Array.isArray(imageIdValue)

          if (fieldType === "image") {
            if (useImageIds) {
              value = imageIdValue
            } else {
              value = formValue?.id ? [formValue.id] : ""
            }
          } else if (fieldType === "file") {
            value = categoryImageId ? [categoryImageId] : ""
          } else {
            value = formValue ?? ""
          }
          return {
            ...field,
            value
          }
        })
        dynamic_fields.push({
          key,
          type,
          label,
          value: sectionFields || [],
          show_listing
        })
      } else if (section.fields?.length === 0) {
        dynamic_fields.push({
          key,
          type,
          label,
          value: [],
          show_listing
        })
      } else if (!["chef", "signature_dishes"].includes(section.key)) {
        const sectionFields = section?.fields?.map((field) => {
          const fieldType = field?.type?.toLowerCase()
          const formValue = fields?.[section.key]?.[field.key]
          const imageIdValue = imageIds?.[key]?.[field.key]
          let value = ""

          const useImageIds = Array.isArray(imageIdValue)

          if (fieldType === "image") {
            if (useImageIds) {
              value = imageIdValue
            } else {
              value = formValue?.id ? [formValue.id] : ""
            }
          } else if (fieldType === "file") {
            value = categoryImageId ? [categoryImageId] : [formValue?.id] || ""
          } else {
            value = formValue ?? ""
          }
          return {
            ...field,
            value
          }
        })
        dynamic_fields.push({
          key,
          type,
          label,
          value: sectionFields,
          show_listing
        })
      }
    })

    const { name = "", images = "", description = "" } = fields?.common || {}

    const payload = {
      dynamic_fields,
      name,
      images: imageIds?.common?.images ? imageIds?.common?.images : [images?.id] || "",
      price: "",
      description,
      did_you_know: getDidYouKnowPayload(values.did_you_know, didYouKnowImage),
      item_history: getHistoryPayload(values.item_history),
      soundbite:
        values?.soundbite !== "" ? (soundbites?.id ? soundbites?.id : values?.soundbite_id) : "",
      soundbite_info: values?.soundbite || "",
      voice_id: values?.soundbite_voice_id || "",
      hotel_config: hotelService?.id,
      nested_nested_subcategory: nestedSubcategoryData?.nested_nested_subcategory_id
    }

    if (!isEdit) {
      dispatch(
        createCategoryItemsAction({
          payload,
          id: linkNestedCategory?.id,
          onClose: handleClose
        })
      )
    } else {
      dispatch(
        editCategoryItemsAction({
          payload,
          id: linkNestedCategory?.id,
          onClose: handleClose
        })
      )
    }
    dispatch(hotelAction.setImageId([]))
  }
  const generateInitialValues = (groupedSections) => {
    let initialValues = {}

    if (isEdit) {
      groupedSections.forEach((section) => {
        const sectionKey = section.key
        const sectionType = section.type
        const dynamicField = categoryItemDetails?.dynamic_fields?.find((f) => f.key === sectionKey)
        const value = dynamicField?.value

        if (sectionKey === "common") {
          initialValues[sectionKey] = {
            name: categoryItemDetails?.name || "",
            images:
              categoryItemDetails?.images && categoryItemDetails?.images?.length
                ? {
                    id: categoryItemDetails?.images[0]?.image_id,
                    url: categoryItemDetails?.images[0]?.url
                  }
                : "",
            price: categoryItemDetails?.price || "",
            description: categoryItemDetails?.description || ""
          }
        } else if (sectionType === "week_time_range") {
          const timming = Array.isArray(value) ? value : []
          initialValues[sectionKey] = days?.map((day) => {
            const match = timming?.find((item) => item?.days === day)
            return (
              match || {
                days: day,
                opening_time: "",
                closing_time: "",
                checked: false
              }
            )
          })
        } else if (sectionType === "daily_hours") {
          const fieldDefaults = {}
          section.fields.forEach((field) => {
            fieldDefaults[field.key] = ""
          })

          initialValues = Array.isArray(value) && value?.length ? value : [fieldDefaults]
        } else if (sectionKey === "signature_dishes") {
          if (Array.isArray(value) && value?.length) {
            initialValues = value?.map((fieldArray) => {
              const updatedItem = {}

              if (Array.isArray(fieldArray)) {
                fieldArray?.forEach((field) => {
                  const fieldKey = field.key
                  const fieldType = field.type
                  const fieldValue = field.value

                  if (fieldType === "image" && Array.isArray(fieldValue)) {
                    updatedItem[fieldKey] = fieldValue?.length ? fieldValue[0] : ""
                  } else {
                    updatedItem[fieldKey] = fieldValue
                  }
                })
              } else {
                const fieldKey = fieldArray?.key
                const fieldType = fieldArray?.type
                const fieldValue = fieldArray?.value

                if (fieldType === "image" && Array.isArray(fieldValue) && fieldValue.length) {
                  updatedItem[fieldKey] = fieldValue[0]
                } else {
                  updatedItem[fieldKey] = fieldValue
                }
              }

              return updatedItem
            })
          } else {
            const fieldDefaults = {}
            section.fields.forEach((field) => {
              fieldDefaults[field.key] = ""
            })
            initialValues = [fieldDefaults]
          }
        } else if (sectionKey === "chef" || sectionKey?.toLowerCase().includes("chef")) {
          if (Array.isArray(value) && value.length) {
            const sectionData = {}

            value.forEach((field) => {
              const fieldKey = field.key
              const fieldType = field.type
              const fieldValue = field.value

              if (fieldType === "image" && Array.isArray(fieldValue) && fieldValue.length) {
                sectionData[fieldKey] = fieldValue[0]
              } else {
                sectionData[fieldKey] = fieldValue
              }
            })

            initialValues[sectionKey] = sectionData
          } else {
            initialValues[sectionKey] = {}
          }
        } else {
          let fieldDefaults = {}

          if (Array.isArray(value) && value.length) {
            fieldDefaults = {}

            value.forEach((field) => {
              const fieldKey = field.key
              const fieldType = field.type
              const fieldValue = field.value

              if (fieldType === "image" && Array.isArray(fieldValue) && fieldValue.length) {
                fieldDefaults[fieldKey] = fieldValue[0]
              } else if (fieldType === "file" && Array.isArray(fieldValue) && fieldValue.length) {
                fieldDefaults[fieldKey] = fieldValue[0]
              } else {
                fieldDefaults[fieldKey] = fieldValue
              }
            })
          } else {
            fieldDefaults = {}
            section.fields?.forEach((field) => {
              fieldDefaults[field.key] = ["ingredients", "uniqueFact"].includes(field.type)
                ? []
                : ""
            })
          }

          initialValues[sectionKey] = fieldDefaults
        }
      })

      return initialValues
    } else {
      groupedSections?.forEach((section) => {
        const sectionKey = section.key
        const sectionType = section.type

        if (!section.fields || section.fields.length === 0) {
          initialValues[sectionKey] = {}
          return
        }

        const fieldDefaults = {}
        section.fields.forEach((field) => {
          fieldDefaults[field.key] = ""
        })

        if (sectionType.includes("week_time_range")) {
          initialValues[sectionKey] = days.map((day) => ({
            days: day,
            opening_time: "",
            closing_time: "",
            checked: false
          }))
        } else if (sectionType.includes("daily_hours")) {
          initialValues = fieldDefaults
        } else if (sectionType.includes("image_Scroll")) {
          initialValues = fieldDefaults
        } else {
          initialValues[sectionKey] = fieldDefaults
        }
      })

      return initialValues
    }
  }

  const fieldsSections = excludeSections(
    {
      keys: ["signature_dishes", "chef"],
      types: ["daily_hours"]
    },
    dynamicFieldsGroup
  )
  const signatureSections = filterSections(["signature_dishes"], dynamicFieldsGroup)
  const chefSections = filterSections(["chef"], dynamicFieldsGroup)
  const hoOperationSections = filterTypeSections(["daily_hours"], dynamicFieldsGroup)

  const generateValidationSchema = (dynamicFieldsGroup) => {
    const fieldsSchemaShape = {}

    const commonSection = dynamicFieldsGroup?.find((section) => section?.key === "common")

    if (commonSection && commonSection.fields?.length) {
      const commonFieldSchema = {}
      commonSection.fields.forEach((field) => {
        if (field.type === "image") {
          commonFieldSchema[field.key] = Yup.mixed()
          return
        } else if (field.key === "price") {
          commonFieldSchema[field.key] = Yup.string()
          return
        } else if (field.key === "name") {
          commonFieldSchema[field.key] = Yup.string()
            .required(`${field.label} is required`)
            .max(40, "Max 40 characters allowed")
          return
        } else {
          commonFieldSchema[field.key] = Yup.string().required(
            `${field.label || field.key} is required`
          )
        }
      })

      fieldsSchemaShape["common"] = Yup.object().shape(commonFieldSchema)
    }

    dynamicFieldsGroup?.forEach((section) => {
      const sectionKey = section.key

      if (section.type === "week_time_range") {
        fieldsSchemaShape[sectionKey] = Yup.array().of(
          Yup.object().shape({
            days: Yup.string(),
            opening_time: Yup.string().when("checked", {
              is: true,
              then: (schema) => schema.required("Opening Time is required"),
              otherwise: (schema) => schema
            }),
            closing_time: Yup.string().when("checked", {
              is: true,
              then: (schema) => schema.required("Closing Time is required"),
              otherwise: (schema) => schema
            }),
            checked: Yup.boolean()
          })
        )
      } else if (sectionKey !== "common") {
        fieldsSchemaShape[sectionKey] = Yup.mixed()
      }
    })

    const chefSchemaShape = {}
    dynamicFieldsGroup
      ?.filter(
        (s) => s?.key?.toLowerCase().includes("chef") || s?.type?.toLowerCase().includes("chef")
      )
      .forEach((section) => {
        const sectionFieldSchema = {}
        section.fields?.forEach((field) => {
          if (field.key === "bio" || field.key?.toLowerCase().includes("bio")) {
            sectionFieldSchema[field.key] = Yup.string().max(200, "Max 200 characters allowed")
          }
        })
        if (Object.keys(sectionFieldSchema).length) {
          chefSchemaShape[section.key] = Yup.object().shape(sectionFieldSchema)
        }
      })

    return Yup.object().shape({
      fields: Yup.object().shape(fieldsSchemaShape),
      signature_dishes: Yup.mixed(),
      chef: Object.keys(chefSchemaShape).length
        ? Yup.object().shape(chefSchemaShape)
        : Yup.object(),
      daily_hours: Yup.mixed()
    })
  }

  const getVisibleSteps = () => {
    const steps = ["fields"]

    const has = (predicate) => dynamicFieldsGroup?.some(predicate)

    if (has((s) => s?.type === "daily_hours")) steps.push("daily_hours")
    if (has((s) => s?.key?.toLowerCase().includes("signature_dish"))) steps.push("signature_dishes")
    if (
      has((s) => s?.key?.toLowerCase().includes("chef") || s.type?.toLowerCase().includes("chef"))
    )
      steps.push("chef")

    return steps
  }

  const visibleSteps = getVisibleSteps()
  const currentStepKey = getFieldsName(activeStep)
  const currentIndex = visibleSteps.indexOf(currentStepKey)
  const isLastStep = currentIndex === visibleSteps.length - 1

  const goToStep = (stepKey) => setActiveStep(stepKeyMap[stepKey])

  const handleNext = async (formik) => {
    const errors = await formik.validateForm()
    const touched = {}

    if (activeStep === 0) {
      const commonErrors = errors?.fields?.common || {}
      const requiredFields = ["name", "price", "description"]
      const hasCommonErrors = requiredFields.some((key) => commonErrors[key])

      if (hasCommonErrors) {
        touched.fields = {
          ...touched.fields,
          common: requiredFields.reduce((acc, key) => ({ ...acc, [key]: true }), {})
        }
      }

      if (dynamicFieldsGroup?.length) {
        const timeTouched = {}

        dynamicFieldsGroup.forEach((section) => {
          if (section.type === "week_time_range") {
            const sectionKey = section.key
            const timeEntries = formik.values.fields?.[sectionKey] || []

            timeEntries.forEach((item, index) => {
              if (item.checked && (!item.opening_time || !item.closing_time)) {
                timeTouched[sectionKey] = timeTouched[sectionKey] || []
                timeTouched[sectionKey][index] = {
                  opening_time: !item.opening_time ? true : undefined,
                  closing_time: !item.closing_time ? true : undefined
                }
              }
            })
          }
        })

        if (Object.keys(timeTouched).length > 0) {
          touched.fields = {
            ...touched.fields,
            ...timeTouched
          }
        }
      }

      if (Object.keys(touched).length > 0) {
        formik.setTouched(touched)
        return
      }
    }

    if (!isLastStep) {
      goToStep(visibleSteps[currentIndex + 1])
    } else {
      formik.handleSubmit()
    }
  }

  const handlePrev = () => {
    if (currentIndex > 0) {
      goToStep(visibleSteps[currentIndex - 1])
    }
  }

  useEffect(() => {
    if (!open) {
      dispatch(marketingActions.resetDidYouKnowImage())
      dispatch(marketingActions.resetHistoryMedia())
      dispatch(marketingActions.setSoundbites({}))
    }
  }, [open])

  return (
    <Formik
      initialValues={{
        fields: generateInitialValues(fieldsSections),
        signature_dishes: isEdit
          ? generateInitialValues(signatureSections)
          : [generateInitialValues(signatureSections)],
        chef: generateInitialValues(chefSections),
        daily_hours: isEdit
          ? generateInitialValues(hoOperationSections)
          : [generateInitialValues(hoOperationSections)],
        did_you_know: isEdit
          ? categoryItemDetails?.did_you_know?.length > 0
            ? [
                ...categoryItemDetails.did_you_know.map((item) => ({
                  heading: item?.title ?? "",
                  content: item?.description ?? "",
                  image: item?.image ?? ""
                }))
              ]
            : [did_you_know_initial_values]
          : [did_you_know_initial_values],
        item_history: isEdit
          ? categoryItemDetails?.item_history
            ? {
                heading: categoryItemDetails?.item_history?.title ?? "",
                content: categoryItemDetails?.item_history?.description ?? ""
              }
            : history_initial_values
          : history_initial_values,
        soundbite: isEdit ? categoryItemDetails?.soundbite_info : "",
        soundbite_url: isEdit ? categoryItemDetails?.soundbite_url : "",
        soundbite_id: isEdit ? categoryItemDetails?.soundbite : "",
        soundbite_voice_id: isEdit ? categoryItemDetails?.soundbite_voice_id : ""
      }}
      enableReinitialize={true}
      validationSchema={generateValidationSchema(dynamicFieldsGroup)}
      onSubmit={(values) => handleSubmitForm(values)}>
      {(formik) => (
        <>
          <StyledDialog open={open} maxWidth="md" fullWidth>
            <Box className="d-flex justify-content-end">
              <IconButton onClick={onClose} sx={style.closeBtn}>
                <Close sx={{ height: "14px", width: "14px" }} />
              </IconButton>
            </Box>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h2" textAlign="center">
                    {isEdit ? `Edit ${title}` : " Add New"}
                  </Typography>
                  <Typography variant="h5" textAlign="center">
                    {stepTitles[activeStep] || stepTitles[4]}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FieldsStepper
                    data={[]}
                    activeIndex={getVisibleSteps().indexOf(getFieldsName(activeStep))}
                    rows={getVisibleSteps().length}
                  />
                </Grid>
                {/* Add New Restaurant Info */}
                <Grid item xs={12}>
                  <AddNewRestaurant
                    data={getStepData(activeStep)}
                    formik={formik}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    onClose={onClose}
                    open={open}
                    type={getFieldsName(activeStep)}
                  />
                </Grid>
              </Grid>
              <Box className="mt-5 d-flex justify-content-around">
                <Grid item xs={12} mt={3} className="d-flex justify-content-center" gap={4}>
                  {activeStep > 0 && (
                    <AppButton
                      sx={{ padding: "10px 20px" }}
                      onClick={handlePrev}
                      startIcon={<ArrowBack />}>
                      Prev
                    </AppButton>
                  )}
                  {!isLastStep ? (
                    <AppButton
                      sx={{ padding: "10px 20px" }}
                      onClick={() => handleNext(formik)}
                      endIcon={<ArrowForward />}>
                      Next
                    </AppButton>
                  ) : (
                    <AppButton sx={{ padding: "10px 50px" }} onClick={formik.handleSubmit}>
                      {isEdit ? buttonText.UPDATE : buttonText.SAVE}
                    </AppButton>
                  )}
                </Grid>
              </Box>
            </DialogContent>
          </StyledDialog>
        </>
      )}
    </Formik>
  )
}

export default FieldsModal
